import type { JSX } from 'react';
import { FormGroup } from 'react-bootstrap';
import Select, { OnChangeValue, Props } from 'react-select';

import { SELECT_MENU_Z_INDEX } from 'Components/FormField/SelectComponentsZIndex';
import { DateFilterHookValues, DateFilterSelectionPreset } from 'Hooks/DateFilter';
import { DateOptionSelectOption } from 'Models/Dashboards';
import { OptionType } from 'Models/Types/GlobalType';

import styles from '../FormField.module.css';

interface FormFieldDateOptionSelectProps {
    dateFilterHookValues: DateFilterHookValues;
    formFieldId: string;
    /** The parent must show a custom date selection modal when this is called. */
    onSelectCustom: () => void;
    options: DateOptionSelectOption[];

    disabled?: boolean;
}

interface GroupedOption {
    readonly label: string;
    readonly options: readonly OptionType[];
}

/** Must be used with `useDateFilter`. */
export const FormFieldDateOptionSelect = (props: FormFieldDateOptionSelectProps): JSX.Element => {
    const convertSelectedOption = (selectedOption: DateOptionSelectOption): OptionType => {
        return groupedOptions[0].options.find((option) => option.value === selectedOption)!;
    };

    const handleChange = (value: OnChangeValue<OptionType, false>): void => {
        if (value!['value'] === DateOptionSelectOption.CUSTOM) {
            props.onSelectCustom();
        } else {
            props.dateFilterHookValues.updateSelection({ option: value!['value'] as DateFilterSelectionPreset });
        }
    };

    const groupedOptions: readonly GroupedOption[] = [
        {
            label: 'Choose Date Range',
            options: props.options.map((option) => ({ value: option, label: option })),
        },
        {
            label: 'Enter a Custom Date',
            options: [
                {
                    value: DateOptionSelectOption.CUSTOM,
                    label: 'Custom Date',
                },
            ],
        },
    ];

    const selectProps: Props<OptionType, false> = {
        options: groupedOptions,
        onChange: handleChange,
        value: convertSelectedOption(props.dateFilterHookValues.selection.option),
        isDisabled: props.disabled,
        inputId: props.formFieldId,
        placeholder: 'Choose a date...',
    };

    return (
        <FormGroup controlId={props.formFieldId} className={styles.formField}>
            <Select
                {...selectProps}
                theme={(theme) => ({
                    ...theme,
                    borderRadius: 9,
                    colors: {
                        ...theme.colors,
                        primary25: '#05263717',
                        text: 'var(--hps-blue)',
                        primary: 'transparent',
                    },
                })}
                styles={{
                    ...styles,
                    menu: (base) => ({
                        ...base,
                        zIndex: SELECT_MENU_Z_INDEX,
                    }),
                    control: (base) => ({
                        ...base,
                        borderColor: '#053c591a',
                        color: 'var(--hps-white)',
                        backgroundColor: 'var(--hps-blue)',
                    }),
                    option: (styles, { isSelected }) => ({
                        ...styles,
                        backgroundColor: isSelected ? '#05263717' : '',
                        color: isSelected ? 'var(--hps-blue)' : '',
                    }),
                    input: (base) => ({
                        ...base,
                        color: 'var(--hps-white)',
                    }),
                    singleValue: (base) => ({
                        ...base,
                        color: 'var(--hps-white)',
                    }),
                    placeholder: (styles) => ({
                        ...styles,
                        color: 'var(--hps-white)',
                    }),
                }}
            />
        </FormGroup>
    );
};
